import React, { useState } from "react";
import { Button, Card, Badge, Avatar, Field, Input, Select, Tabs, Drawer, Icon, IconBtn, EmptyState, PageHeader, Table, Spinner, Alert, Stat } from "../../design-system";

// =====================================================================
// Empty states + Loading + Error showcase page
// =====================================================================
function EstadosShowcase() {
  document.title = "Estados | Kronaly";
  return (
    <div className="kr-page">
      <div style={{ padding: "24px 32px", display: "flex", flexDirection: "column", gap: 20 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 4 }}>Sistema de diseño</div>
          <h1 style={{ margin: 0, fontFamily: "var(--kr-font-display)", fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--kr-fg-strong)" }}>Estados vacíos, carga y error</h1>
          <p style={{ margin: "6px 0 0", fontSize: 14, color: "var(--kr-fg-muted)", maxWidth: 640 }}>
            Plantillas reutilizables para situaciones sin datos, en proceso o con error.
          </p>
        </div>

        {/* Empty states */}
        <SectionTitle title="Estados vacíos" hint="Cada uno con un mensaje claro y una acción primaria." />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
          <EmptyShowcase
            glyph={<UsersGlyph />}
            title="Aún no hay empleados"
            subtitle="Importa tu plantilla desde un CSV o añade el primer empleado manualmente."
            primary="Añadir empleado"
            secondary="Importar CSV"
          />
          <EmptyShowcase
            glyph={<InboxGlyph />}
            title="Bandeja al día"
            subtitle="No hay solicitudes pendientes. Cuando llegue una nueva, aparecerá aquí."
            primary={null}
            secondary="Ver historial"
          />
          <EmptyShowcase
            glyph={<DocsGlyph />}
            title="Sin documentos todavía"
            subtitle="Sube el primer documento o solicítaselo a un empleado."
            primary="Subir documento"
            secondary="Solicitar"
          />
          <EmptyShowcase
            glyph={<ReportsGlyph />}
            title="No hay reportes generados"
            subtitle="Genera tu primer reporte mensual cuando cierres un periodo de jornada."
            primary="Generar reporte"
            secondary={null}
          />
          <EmptyShowcase
            glyph={<AlertGlyph />}
            title="Sin incidencias"
            subtitle="Tu equipo está cumpliendo el horario sin problemas detectados."
            primary={null}
            secondary="Ajustar tolerancias"
            tone="success"
          />
          <EmptyShowcase
            glyph={<ContractGlyph />}
            title="No hay contratos activos"
            subtitle="Cada empleado necesita un contrato para definir sus reglas de jornada."
            primary="Crear contrato"
            secondary={null}
          />
        </div>

        {/* Loading states */}
        <SectionTitle title="Estados de carga" hint="Skeletons en lugar de spinners para que el layout no salte." />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)", marginBottom: 10 }}>Skeleton — tabla</div>
            <SkeletonTable rows={5} />
          </Card>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)", marginBottom: 10 }}>Skeleton — métricas</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
              {[0,1,2].map(i => <SkeletonStat key={i} />)}
            </div>
            <div style={{ marginTop: 18 }}>
              <Shimmer width="40%" height={14} />
              <Shimmer width="100%" height={120} style={{ marginTop: 10 }} />
            </div>
          </Card>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)", marginBottom: 14 }}>Spinner inline</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 24, justifyContent: "center" }}>
              <ShowcaseSpinner />
              <span style={{ fontSize: 13, color: "var(--kr-fg-muted)" }}>Generando reporte de Enero 2026…</span>
            </div>
          </Card>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)", marginBottom: 14 }}>Progress bar</div>
            <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
              <span style={{ color: "var(--kr-fg-muted)" }}>Subiendo contrato.pdf</span>
              <span style={{ fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-strong)", fontWeight: 600 }}>64%</span>
            </div>
            <div style={{ height: 8, background: "var(--kr-neutral-100)", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ width: "64%", height: "100%", background: "var(--kr-action)", borderRadius: 4 }} />
            </div>
            <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 6 }}>262 KB / 412 KB · 2 segundos restantes</div>
          </Card>
        </div>

        {/* Error states */}
        <SectionTitle title="Estados de error" hint="Lenguaje claro, posibles causas y acción de recuperación." />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <ErrorState
            icon="AlertTriangle"
            tone="danger"
            title="No hemos podido cargar las jornadas"
            subtitle="Hubo un problema al conectar con el servidor de marcajes. El estado en vivo puede estar desactualizado."
            primary="Reintentar"
            secondary="Reportar problema"
          />
          <ErrorState
            icon="LogOut"
            tone="warning"
            title="No tienes acceso a esta sección"
            subtitle="Tu rol actual (Manager) no permite ver datos salariales. Contacta con un Admin si necesitas acceso."
            primary="Ver mi perfil"
            secondary="Contactar Admin"
          />
          <ErrorState
            icon="AlertTriangle"
            tone="warning"
            title="Sin conexión a internet"
            subtitle="Tus marcajes se están guardando localmente. Se sincronizarán cuando recuperes conexión."
            primary="Reintentar"
            secondary={null}
            banner
          />
          <ErrorState
            icon="XCircle"
            tone="danger"
            title="No se ha encontrado el reporte"
            subtitle="Es posible que se haya eliminado o que el enlace haya caducado."
            primary="Ir a Reportes"
            secondary={null}
          />
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ title, hint }) {
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--kr-fg-strong)", letterSpacing: "-0.01em" }}>{title}</div>
      {hint && <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function EmptyShowcase({ glyph, title, subtitle, primary, secondary, tone }) {
  return (
    <Card>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "16px 12px 8px", gap: 12 }}>
        <div style={{ width: 64, height: 64, borderRadius: 16, background: tone === "success" ? "var(--kr-status-success-bg)" : "var(--kr-action-soft-bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {glyph}
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--kr-fg-strong)" }}>{title}</div>
          <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 4, maxWidth: 260, textWrap: "pretty" }}>{subtitle}</div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          {primary && <Button variant="primary" size="sm" icon="Plus">{primary}</Button>}
          {secondary && <Button variant="secondary" size="sm">{secondary}</Button>}
        </div>
      </div>
    </Card>
  );
}

function ErrorState({ icon, tone, title, subtitle, primary, secondary, banner }) {
  const c = tone === "danger" ? { bg: "var(--kr-status-danger-bg)", fg: "var(--kr-danger-700)" } : { bg: "var(--kr-status-warning-bg)", fg: "var(--kr-warning-700)" };
  if (banner) {
    return (
      <Card style={{ background: c.bg, border: `1px solid ${c.bg}` }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <Icon name={icon} size={18} style={{ color: c.fg, flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: c.fg }}>{title}</div>
            <div style={{ fontSize: 12, color: c.fg, marginTop: 2, opacity: 0.9 }}>{subtitle}</div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              {primary && <Button variant="secondary" size="sm">{primary}</Button>}
            </div>
          </div>
        </div>
      </Card>
    );
  }
  return (
    <Card>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "16px 12px 8px", gap: 12 }}>
        <div style={{ width: 56, height: 56, borderRadius: 999, background: c.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name={icon} size={22} style={{ color: c.fg }} />
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--kr-fg-strong)" }}>{title}</div>
          <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 4, maxWidth: 320, textWrap: "pretty" }}>{subtitle}</div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          {primary && <Button variant="primary" size="sm">{primary}</Button>}
          {secondary && <Button variant="ghost" size="sm">{secondary}</Button>}
        </div>
      </div>
    </Card>
  );
}

// Skeletons
function Shimmer({ width = "100%", height = 12, style = {} }) {
  return (
    <div style={{
      width, height, borderRadius: height > 24 ? 8 : 4,
      background: "linear-gradient(90deg, var(--kr-neutral-100), var(--kr-neutral-150), var(--kr-neutral-100))",
      backgroundSize: "200% 100%",
      animation: "krShimmer 1.4s ease-in-out infinite",
      ...style,
    }} />
  );
}

function SkeletonTable({ rows = 5 }) {
  return (
    <div>
      {[...Array(rows)].map((_, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: i < rows - 1 ? "1px solid var(--kr-border-subtle)" : "none" }}>
          <Shimmer width={28} height={28} style={{ borderRadius: 999, flexShrink: 0 }} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <Shimmer width="40%" height={10} />
            <Shimmer width="65%" height={8} />
          </div>
          <Shimmer width={60} height={20} style={{ borderRadius: 999 }} />
          <Shimmer width={40} height={12} />
        </div>
      ))}
    </div>
  );
}

function SkeletonStat() {
  return (
    <div style={{ padding: 14, border: "1px solid var(--kr-border)", borderRadius: 8 }}>
      <Shimmer width="50%" height={9} />
      <Shimmer width="65%" height={22} style={{ marginTop: 10 }} />
      <Shimmer width="40%" height={9} style={{ marginTop: 10 }} />
    </div>
  );
}

function ShowcaseSpinner() {
  return (
    <div style={{
      width: 22, height: 22, borderRadius: 999,
      border: "2.5px solid var(--kr-neutral-150)",
      borderTopColor: "var(--kr-action)",
      animation: "krSpin .9s linear infinite",
    }} />
  );
}

// Glyphs
function UsersGlyph() { return <Icon name="Users" size={28} style={{ color: "var(--kr-action)" }} />; }
function InboxGlyph() { return <Icon name="Inbox" size={28} style={{ color: "var(--kr-action)" }} />; }
function DocsGlyph() { return <Icon name="FileText" size={28} style={{ color: "var(--kr-action)" }} />; }
function ReportsGlyph() { return <Icon name="TrendingUp" size={28} style={{ color: "var(--kr-action)" }} />; }
function AlertGlyph() { return <Icon name="CheckCircle2" size={28} style={{ color: "var(--kr-success-700)" }} />; }
function ContractGlyph() { return <Icon name="FileText" size={28} style={{ color: "var(--kr-action)" }} />; }

export default EstadosShowcase;
